import { SetStateAction, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { setIntercomMessengerHidden, updateTitle } from '../slices/ui';
import { useAppDispatch } from '../store/hooks';
import { readStoredValue, writeStoredValue } from '../utils/storage';

/** Set the browser tab's title (`<title>` tag) */
export function usePageTitle(pageTitle?: string) {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(updateTitle(pageTitle));
  }, [dispatch, pageTitle]);
}

/** Hide Intercom chat button when opening a modal from the right */
export function useIntercomInModal(showModal: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (showModal) {
      dispatch(setIntercomMessengerHidden(true));
    } else {
      dispatch(setIntercomMessengerHidden(false));
    }
    return () => {
      dispatch(setIntercomMessengerHidden(false));
    };
  }, [dispatch, showModal]);
}

/**
 * An alternative to `useState`.
 *
 * `usePatchedState` is a wrapper around `useReducer` for our common use-case of patching the keys of an object in
 * state. It also allows the return types to be correct more simply.
 *
 * Usage:
 *
 * ```ts
 * const [state, patch] = usePatchedState({ name: '', age: 0 }); // `state` and `patch` are typed correctly
 * patch({ age: 40 });
 * patch({ name: 'Ludo' });
 *
 * type State = { name: string; age?: number };
 * const [stateWithExplicitType, patch2] = usePatchedState<State>({ name: '' });
 * ```
 *
 * You can also get the current state by calling `patch` with a function:
 *
 * ```ts
 * patch((state) => ({ age: state.age + 1 }));
 * ```
 */
export function usePatchedState<State extends Record<string, unknown>>(initialState: State) {
  return useReducer(
    (state: State, patch: Partial<State> | ((state: State) => Partial<State>)): State => ({
      ...state,
      ...(typeof patch === 'function' ? patch(state) : patch),
    }),
    initialState,
  );
}

/**
 * A `useState` remembered across sessions under `key`, read and written by `readStoredValue` and `writeStoredValue`.
 * Only the setter writes, so a value never set keeps following its default.
 */
export function useStoredState<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState(() => readStoredValue(key, defaultValue));
  // successive calls within one render each build on the previous one, as with `useState`
  const valueRef = useRef(value);

  const setStoredValue = useCallback(
    (action: SetStateAction<T>) => {
      const next = typeof action === 'function' ? (action as (previous: T) => T)(valueRef.current) : action;
      valueRef.current = next;
      writeStoredValue(key, next);
      setValue(next);
    },
    [key],
  );

  return [value, setStoredValue] as const;
}

/**
 * Observe page width changes
 *
 * @param callback Called when the width of the page changes
 */
export function useDocumentWidth(callback: (width: number) => void) {
  useEffect(() => {
    const observer = new ResizeObserver((a) => {
      if (a.length === 1) {
        const width = a[0]!.borderBoxSize[0]?.inlineSize ?? 0;
        callback(width);
      }
    });
    observer.observe(document.body);

    return () => observer.disconnect();
  }, [callback]);
}

export function useResizeObserver(element: HTMLElement | null, callback: (entry: ResizeObserverEntry) => void) {
  useEffect(() => {
    if (!element) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      if (entries.length === 1) {
        callback(entries[0]!);
      }
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, callback]);
}

export function useDebouncedValue<Value>(value: Value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}
